import React from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Zap, Bot, ShoppingBag, MessageSquare, QrCode, Scissors, Swords, Check, ArrowRight } from "lucide-react";
import { Brand } from "@/components/AppLayout";
import { useLang } from "@/i18n";
import { useTheme } from "@/theme";
import { useAuth } from "@/context/AuthContext";
import { Languages, Sun, Moon } from "lucide-react";

const flow = ["Go Live", "Engagement", "AI Producer", "Lead", "Product", "Order", "Revenue"];

const features = [
  { icon: Radio, title: "Browser Broadcast Studio", d: "Real WebRTC camera & mic, screen share, guests, 8-scene builder, overlays, lower-third & ticker — no install." },
  { icon: Bot, title: "OTO AI Producer", d: "A producer in your ear: engagement telemetry, pricing-query alerts, and one-tap Show Product / CTA actions." },
  { icon: ShoppingBag, title: "Live Commerce", d: "Offer, Product & Property cards in RM. Seats, stock and prices come straight from your product database." },
  { icon: MessageSquare, title: "Unified Comments + AI", d: "Facebook, YouTube & TikTok in one feed, auto-classified: Question · Buying Intent · Complaint · Spam · Hot Lead." },
  { icon: QrCode, title: "Live Lead Magnet", d: "Unique QR per session → landing page → CRM. Every lead attributed to session, host & product." },
  { icon: Scissors, title: "AI Clip Generator", d: "Turn a 2-hour live into 10 shorts, Reels & TikTok candidates with captions, titles & hashtags." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang, toggle: toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const { user } = useAuth();

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between">
        <Brand onClick={() => navigate("/")} />
        <div className="flex items-center gap-2">
          <button data-testid="language-toggle-btn" onClick={toggleLang} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border hover:bg-accent text-xs font-semibold font-mono uppercase">
            <Languages className="h-4 w-4" /> {lang}
          </button>
          <button data-testid="theme-toggle-btn" onClick={toggleTheme} className="flex items-center justify-center h-9 w-9 rounded-lg border border-border hover:bg-accent">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button data-testid="nav-pricing-link" onClick={() => navigate("/pricing")} className="hidden sm:block h-9 px-4 rounded-lg text-sm font-medium hover:bg-accent">{t("nav_pricing")}</button>
          {user ? (
            <button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")} className="h-9 px-4 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white text-sm font-bold">Dashboard</button>
          ) : (
            <button data-testid="login-google-btn" onClick={login} className="h-9 px-4 rounded-lg bg-[#E5253A] hover:bg-[#c71f31] text-white text-sm font-bold">{t("login_google")}</button>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-40" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#E5253A]/20 blur-3xl" />
        <div className="absolute top-40 -left-32 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-xs font-mono mb-6">
              <span className="h-2 w-2 rounded-full bg-emerald-400 live-dot" /> YES GO LIVE · {t("tagline")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {t("hero_title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-get-started-btn" onClick={user ? () => navigate("/dashboard") : login} className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#E5253A] hover:bg-[#c71f31] text-white font-bold">
                <Zap className="h-5 w-5" /> {user ? "Open Dashboard" : t("get_started")}
              </button>
              <button data-testid="hero-pricing-btn" onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-border hover:bg-accent font-semibold">
                {t("nav_pricing")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs font-mono text-muted-foreground">
              {flow.map((f, i) => (
                <span key={f} className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded ${i === flow.length - 1 ? "bg-emerald-500/15 text-emerald-400" : "bg-accent"}`}>{f}</span>
                  {i < flow.length - 1 && <ArrowRight className="h-3 w-3 opacity-50" />}
                </span>
              ))}
            </div>
          </div>

          {/* Mock control room */}
          <div className="fade-in">
            <div className="rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-background/40">
                <div className="flex items-center gap-2 text-xs font-mono"><span className="h-2 w-2 rounded-full bg-[#E5253A] live-dot" /> ON AIR <span className="text-muted-foreground">01:24:37</span></div>
                <div className="flex gap-1.5">
                  {["FB", "YT", "TT"].map((p) => <span key={p} className="px-2 py-0.5 rounded bg-accent text-[10px] font-mono">{p}</span>)}
                </div>
              </div>
              <div className="relative aspect-video bg-gradient-to-br from-[#11141C] to-[#1b2030] flex items-center justify-center">
                <div className="absolute top-3 left-3 px-2 py-1 rounded bg-[#E5253A] text-white text-[10px] font-bold">● LIVE</div>
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-[#E5253A]/20 flex items-center justify-center mx-auto"><Radio className="h-7 w-7 text-[#E5253A]" /></div>
                  <div className="mt-2 text-xs text-muted-foreground font-mono">Studio Preview</div>
                </div>
                <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-lg bg-black/60 text-white">
                  <div className="text-xs font-bold">Ustaz Amir</div>
                  <div className="text-[10px] opacity-80">Umrah Specialist</div>
                </div>
                <div className="absolute bottom-3 right-3 px-3 py-2 rounded-lg bg-amber-500/90 text-black">
                  <div className="text-[9px] font-bold">LIVE OFFER</div>
                  <div className="text-sm font-mono font-bold">RM 8,990</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-px bg-border">
                {[["Viewers", "4,281"], ["Comments", "728"], ["Leads", "94"], ["Revenue", "RM31.2k"]].map(([k, v]) => (
                  <div key={k} className="bg-card p-3 text-center">
                    <div className="text-sm font-mono font-bold">{v}</div>
                    <div className="text-[10px] text-muted-foreground">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Not a StreamYard clone.</h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">The studio is just the base. Yes Go Live adds the AI Producer, Commerce, CRM, Lead Attribution, Revenue, Battle & content repurposing.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {features.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`} className="rounded-xl border border-border bg-card p-6 hover:border-[#E5253A]/40 transition-colors">
              <div className="h-11 w-11 rounded-lg bg-[#E5253A]/10 flex items-center justify-center"><f.icon className="h-5 w-5 text-[#E5253A]" /></div>
              <h3 className="mt-4 text-lg font-bold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-[#E5253A]/10 to-transparent p-8 sm:p-12 text-center">
          <Swords className="h-8 w-8 mx-auto text-[#E5253A]" />
          <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight">Go Live. Engage. Convert.</h2>
          <p className="mt-2 text-muted-foreground">Start free — 720p, 1 destination, AI Producer preview.</p>
          <button data-testid="cta-start-btn" onClick={user ? () => navigate("/dashboard") : login} className="mt-6 inline-flex items-center gap-2 h-12 px-8 rounded-xl bg-[#E5253A] hover:bg-[#c71f31] text-white font-bold">
            <Zap className="h-5 w-5" /> {user ? "Open Dashboard" : t("get_started")}
          </button>
        </div>
      </section>
    </div>
  );
}
